// client/src/components/MerchantInvoices.js

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { firestore, storage, storageFallback, getDownloadURLSmart } from '../../firebase';
import { doc, collection, query, where, getDocs, onSnapshot, updateDoc, limit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, listAll, deleteObject, uploadBytes, getMetadata } from 'firebase/storage';
import '../CSS/Dashboard.css';
import JSZip from 'jszip';
import { useNavigate } from 'react-router-dom';
import MerchantNavbar from './MerchantNavbar';
import { FaDownload, FaTrash, FaCalendarAlt, FaUser, FaImage, FaCog, FaArrowLeft, FaList, FaTh, FaUpload, FaTimes } from 'react-icons/fa';
import { useDataContext } from '../../DataContext';
import { sanitize } from '../../utils/sanitize';
import { listCompanyShares } from '../../utils/shareStore';

// util: clé unique pour le cache
const photoKey = (ownerUid, name) => `${ownerUid}__${name}`;

// util: normalisation (assure ownerUid + path + name présents)
const normalizePhoto = (p = {}) => {
  const pathOwner = p.path?.split('/')?.[0];
  const owner = p.ownerUid || pathOwner || 'unknown';
  const name = p.name || (p.path ? p.path.split('/').pop() : 'untitled');
  const path = p.path || `${owner}/photos/${name}`;
  return { ...p, ownerUid: owner, name, path };
};

// util: constr. objet photo depuis meta
const buildPhoto = (ownerUid, itemName, url, customMetadata, timeCreated) => {
  const createdAt = customMetadata?.createdAt ? new Date(Number(customMetadata.createdAt)) :
                     timeCreated ? new Date(timeCreated) : new Date();
  const timestamp = createdAt.getTime();
  const formattedDate = createdAt.toLocaleString('fr-FR', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const employeePseudo = sanitize(customMetadata?.TakenByPseudo) || (ownerUid ? 'Inconnu' : 'Moi');

  return {
    name: itemName,
    path: `${ownerUid}/photos/${itemName}`,   // pour delete fiable
    ownerUid,
    url,
    date: formattedDate,
    timestamp,
    employeePseudo,
    metadata: customMetadata || {},
  };
};

const MerchantInvoices = () => {
  const { photo } = window.location.state || {};
  const [user, setUser] = useState(null);
  const [photos, setPhotos] = useState([]);
  const navigate = useNavigate();
  // eslint-disable-next-line
  const [invitations, setInvitations] = useState([]);
  // eslint-disable-next-line
  const [showModal, setShowModal] = useState(false);
  const { fetchData, dataCache } = useDataContext();
  // eslint-disable-next-line
  const [data, setData] = useState(null);
  const [isGridView, setIsGridView] = useState(true);
  // eslint-disable-next-line
  const [photoTitle, setPhotoTitle] = useState('');
  // eslint-disable-next-line
  const [isEditing, setIsEditing] = useState(false);

  const fileInputRef = useRef(null);
  const lastFetchedUidRef = useRef(null); // permet un fetch auto après refresh
  const cacheMap = useRef(null);   // map du cache local pour accès O(1)

  // NEW (coût) : état modale + debounce/refs pour limiter les writes Firestore du toggle
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const monthLabels = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth()); // 0-11
  const [selectedYear, setSelectedYear] = useState(now.getFullYear()); // ex: 2025

  // Debounce & cache de la dernière valeur sauvegardée pour lastViewMode
  const viewModeSaveTimer = useRef(null);
  const lastSavedViewModeRef = useRef(null);
  const latestViewModeRef = useRef(isGridView ? 'grid' : 'table'); // valeur courante choisie (évite les effets de fermeture)

  // Précharge le dernier mode affichage depuis localStorage pour limiter le flicker avant snapshot user
  useEffect(() => {
    try {
      const storedMode = localStorage.getItem('lastViewMode');
      if (storedMode === 'grid' || storedMode === 'table') {
        setIsGridView(storedMode === 'grid');
        latestViewModeRef.current = storedMode;
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  // Sauvegarde immédiate (utilisée par debounce et flush unmount)
  const saveViewModeNow = useCallback(async (modeStr) => {
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      // n’envoie la write que si la valeur a réellement changé par rapport à la dernière sauvée serveur
      if (userId && lastSavedViewModeRef.current !== modeStr) {
        const userDocRef = doc(firestore, 'users', userId);
        await updateDoc(userDocRef, { lastViewMode: modeStr });
        lastSavedViewModeRef.current = modeStr;
      }
    } catch (e) {
      // on reste silencieux pour ne pas impacter l’UX
      console.error('Maj lastViewMode ignorée (optimisation coût):', e);
    }
  }, []);

  // Responsive
  const [isNarrow, setIsNarrow] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 770 : false));
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 770);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Charge cache local une fois
  useEffect(() => {
    const stored = localStorage.getItem('userPhotos');
    if (stored) {
      try {
        const arr = JSON.parse(stored);
        const normalized = Array.isArray(arr) ? arr.map(normalizePhoto) : [];
        setPhotos(normalized);
        try { localStorage.setItem('userPhotos', JSON.stringify(normalized)); } catch {}
        // construit une map pour lookup rapide
        const m = new Map();
        for (const p of normalized) m.set(photoKey(p.ownerUid, p.name), p);
        cacheMap.current = m;
      } catch {}
    } else {
      cacheMap.current = new Map();
    }
  }, []);

  // Récupération invitations (inchangé)
  const fetchInvitations = async (email) => {
    // Firestore rules require a limit <= 50 for list queries on "entreprises"
    // (see firestore.rules: match /entreprises/{entId} → allow list if request.query.limit <= 50).
    // Without a limit the query is denied with "Missing or insufficient permissions".
    const entrepriseRef = collection(firestore, 'entreprises');
    const qy = query(
      entrepriseRef,
      where('employeeEmail', '==', email),
      where('status', '==', 'pending'),
      // defensive cap to satisfy security rule and avoid large scans
      limit(50)
    );
    const querySnapshot = await getDocs(qy);
    const fetchedInvitations = querySnapshot.docs.map((docu) => ({ id: docu.id, ...docu.data() }));
    setInvitations(fetchedInvitations);
  };

  // fetchPhotos: delta fetch (ne télécharge URL + metadata QUE pour les nouveaux fichiers)
  const fetchPhotos = useCallback(async () => {
    try {
      const auth = getAuth();
      const curr = auth.currentUser;
      if (!curr) return;

      const userId = curr.uid;
      if (!userId) return;

      const newPhotos = [];
      const cached = cacheMap.current || new Map();

      // Dossier principal de l'entreprise
      const baseRef = ref(storage, `${userId}/photos/`);
      let listRes;
      try {
        listRes = await listAll(baseRef);
      } catch (err) {
        // Si le dossier n'existe pas dans le bucket principal, tente le bucket fallback
        if (storageFallback) {
          try {
            listRes = await listAll(ref(storageFallback, `${userId}/photos/`));
          } catch {
            listRes = { items: [] };
          }
        } else {
          listRes = { items: [] };
        }
      }
      for (const item of listRes.items) {
        const k = photoKey(userId, item.name);
        const cachedItem = cached.get(k);
        if (cachedItem) {
          newPhotos.push({
            ...cachedItem,
            ownerUid: cachedItem.ownerUid || userId,
            path: cachedItem.path || `${userId}/photos/${item.name}`,
          });
          continue;
        }
        try {
          const [url, meta] = await Promise.all([
            getDownloadURLSmart(item.fullPath || item),
            getMetadata(item)
          ]);
          const cm = meta?.customMetadata || {};
          const p = buildPhoto(userId, item.name, url, cm, meta?.timeCreated);
          newPhotos.push(p);
          cached.set(k, p);
        } catch (err) {
          console.error('Erreur fetch photo commerçant:', err);
        }
      }

      // Photos partagées par les salariés (Firestore → Storage)
      const sharedEntries = await listCompanyShares(userId);
      for (const entry of sharedEntries) {
        const fileName = entry.fileName || entry.name;
        if (!fileName) continue;
        const key = photoKey(entry.ownerUid || 'unknown', fileName);
        const storagePath = entry.storagePath || `${entry.ownerUid}/photos/${fileName}`;
        const cachedItem = cached.get(key);

        if (cachedItem) {
          newPhotos.push({
            ...cachedItem,
            ownerUid: cachedItem.ownerUid || entry.ownerUid || storagePath.split('/')?.[0] || 'unknown',
            path: cachedItem.path || storagePath,
          });
          continue;
        }

        try {
          const photoRef = ref(storage, storagePath);
          const [url, meta] = await Promise.all([
            getDownloadURLSmart(storagePath),
            getMetadata(photoRef).catch(() => null),
          ]);
          const cm = meta?.customMetadata || {};
          const ownerUid = entry.ownerUid || storagePath.split('/')?.[0] || 'unknown';
          const p = {
            ...buildPhoto(
              ownerUid,
              fileName,
              url,
              cm,
              meta?.timeCreated || entry.timestamp
            ),
            path: storagePath,
            ownerUid,
          };
          newPhotos.push(p);
          cached.set(key, p);
        } catch (err) {
          console.error('Erreur fetch photo partagé:', err);
        }
      }

      // normalise/dédoublonne sur path (clé la plus fiable)
      const normalized = newPhotos.map(normalizePhoto);

      const deduped = Array.from(
        new Map(normalized.map((p) => [p.path || `${p.ownerUid}-${p.name}`, p])).values()
      );

      deduped.sort((a, b) => b.timestamp - a.timestamp);
      setPhotos(deduped);
      localStorage.setItem('userPhotos', JSON.stringify(deduped));
      cacheMap.current = new Map(deduped.map((p) => [photoKey(p.ownerUid, p.name), p]));
    } catch (error) {
      console.error('Erreur lors de la récupération des photos :', error);
      alert("Impossible de récupérer les photos.");
    }
  }, []);

  // Auth + onSnapshot (on évite de relancer fetchPhotos à chaque petit changement du doc user)
  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, async (u) => {
      if (u) {
        const userId = u?.uid;
        if (userId) {
          const userDocRef = doc(firestore, 'users', userId);

          const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
            const userData = docSnapshot.data();
            setUser({ ...userData, pseudo: userData?.pseudo });

            if (!userData?.pseudo) setShowModal(true);

            fetchInvitations(userData?.email);

            // fetchData business as is
            fetchData('merchantInvoices')
              .then(fetchedData => {
                setData(fetchedData);
                if (fetchedData && fetchedData.length > 0) {
                  localStorage.setItem('merchantInvoices', JSON.stringify(fetchedData));
                }
              })
              .catch(error => {
                console.error('Erreur lors de la récupération des données merchantInvoices:', error);
              });

            if (userData?.lastViewMode && userData.lastViewMode !== lastSavedViewModeRef.current) {
              setIsGridView(userData.lastViewMode === 'grid');
              lastSavedViewModeRef.current = userData.lastViewMode; // mémorise la valeur serveur appliquée
              try { localStorage.setItem('lastViewMode', userData.lastViewMode); } catch {}
            }

          });

          return () => unsubscribe();
        }
      } else {
        navigate('/');
      }
    });

    // Si on arrive d’un clic sur une photo précise
    if (photo && !isEditing) {
      const storedPhotos = JSON.parse(localStorage.getItem('userPhotos') || '[]');
      const localPhoto = storedPhotos?.find((p) => p.name === photo.name);
      if (localPhoto?.metadata?.title) {
        setPhotoTitle(localPhoto.metadata.title);
      } else if (photo.metadata?.title) {
        setPhotoTitle(photo.metadata.title);
      } else {
        // si jamais title absent: tenter une seule fois
        (async () => {
          try {
            const uid = getAuth().currentUser?.uid;
            if (!uid) return;
            const photoRef = ref(storage, `${uid}/photos/${photo.name}`);
            const metadata = await getMetadata(photoRef); // appel ponctuel
            if (sanitize(metadata.customMetadata?.title)) {
              setPhotoTitle(metadata.customMetadata.title);
            }
          } catch (e) {
            console.error('Erreur meta Firebase:', e);
          }
        })();
      }
    }

    // Cleanup: clear debounce timer à l’unmount
    return () => {
      if (typeof unsubscribeAuth === 'function') unsubscribeAuth();
      if (viewModeSaveTimer.current) {
        clearTimeout(viewModeSaveTimer.current);
        // flush la dernière valeur choisie si elle n'a pas été écrite
        saveViewModeNow(latestViewModeRef.current);
      }
    };
  }, [navigate, fetchPhotos, fetchData, dataCache, isEditing, photo, saveViewModeNow]);

  // Après chargement des infos utilisateur, on force un fetch (utile après refresh)
  useEffect(() => {
    if (!user?.uid) return;
    if (lastFetchedUidRef.current === user.uid) return;
    lastFetchedUidRef.current = user.uid;
    fetchPhotos();
  }, [user?.uid, fetchPhotos]);

  const handleViewChange = async () => {
    const newView = !isGridView;
    setIsGridView(newView);

    const modeStr = newView ? 'grid' : 'table';
    latestViewModeRef.current = modeStr;
    try { localStorage.setItem('lastViewMode', modeStr); } catch {}

    // annule un éventuel timer précédent
    if (viewModeSaveTimer.current) {
      clearTimeout(viewModeSaveTimer.current);
    }
    // on attend un petit délai : si l’utilisateur re-clique, une seule écriture sera envoyée
    viewModeSaveTimer.current = setTimeout(() => {
      saveViewModeNow(modeStr);
    }, 800);
  };

  // garde en phase latestViewModeRef avec l'état React (utile pour flush unmount)
  useEffect(() => {
    latestViewModeRef.current = isGridView ? 'grid' : 'table';
  }, [isGridView]);

  // Import facture
  const handleImportClick = () => fileInputRef.current?.click();

  const handleImportInvoice = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image (PNG, JPG, HEIC, etc.)');
      return;
    }
    try {
      const auth = getAuth();
      const userId = auth.currentUser?.uid;
      if (!userId) {
        alert("Utilisateur non connecté.");
        return;
      }
      const fileName = file.name;
      const storageRef = ref(storage, `${userId}/photos/${fileName}`);

      await uploadBytes(storageRef, file, {
        customMetadata: {
          TakenByPseudo: user?.pseudo || 'Moi',
          title: fileName.replace(/\.[^/.]+$/, ''),
          category: userId,
          createdAt: String(Date.now()), // évite d'appeler getMetadata plus tard
          ownerUid: userId,
        },
        contentType: file.type,
        cacheControl: 'public, max-age=604800, immutable',
      });

      // ⬇️ Recharge les photos pour faire apparaître la nouvelle facture immédiatement
      await fetchPhotos();

      event.target.value = null;
    } catch (error) {
      console.error('Erreur lors de l’import de la photo :', error);
      alert('Erreur lors de l’import de la photo.');
    }
  };

  const handleDownloadPhoto = async (p) => {
    try {
      const response = await fetch(p.url);
      const blob = await response.blob();

      const metadata = p.metadata || {};
      const title = metadata.title ? metadata.title.toUpperCase().replace(/\s+/g, '_') : null;
      const date = p.timestamp ? new Date(p.timestamp).toLocaleDateString('fr-FR').replace(/\//g, '-') : null;
      const employeePseudo = metadata.TakenByPseudo ? metadata.TakenByPseudo.toUpperCase().replace(/\s+/g, '_') : null;

      let fileName = "FACTURE.png";
      if (date && title && employeePseudo) fileName = `${date}-${title}-${employeePseudo}.png`;
      else if (date && title) fileName = `${date}-${title}.png`;
      else if (date && employeePseudo) fileName = `${date}-${employeePseudo}.png`;
      else if (date) fileName = `${date}.png`;
      else if (title) fileName = `${title}.png`;
      else if (employeePseudo) fileName = `${employeePseudo}.png`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement de la photo:', error);
    }
  };

  // Suppression fiable via path
  const handleDeletePhoto = async (p) => {
    try {
      const photoRef = ref(storage, p.path);
      await deleteObject(photoRef);

      const updated = photos.filter((x) => !(x.ownerUid === p.ownerUid && x.name === p.name));
      setPhotos(updated);
      localStorage.setItem('userPhotos', JSON.stringify(updated));
      cacheMap.current = new Map(updated.map(x => [photoKey(x.ownerUid, x.name), x]));

      alert('Photo supprimée avec succès.');
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        console.warn("La photo n'existe plus dans Firebase Storage.");
        alert("Cette photo n'existe plus.");
      } else {
        console.error('Erreur lors de la suppression de la photo:', error);
        alert('Erreur lors de la suppression de la photo.');
      }
    }
  };

  // === NOUVELLE OUVERTURE DE MODALE (UI refaite) ===
  const handleDownloadPhotosByMonth = () => {
    // on ouvre simplement la modale React (nouveau look), aucun changement de logique ailleurs
    setIsBulkModalOpen(true);
  };

  // === ACTION DE CONFIRMATION (identique à l'ancien comportement) ===
  const confirmBulkDownload = async () => {
    try {
      const monthIndex = Number(selectedMonth); // 0-11
      const yearInt = Number(selectedYear);

      const filteredPhotos = photos.filter((ph) => {
        const d = new Date(ph.timestamp);
        return d.getMonth() === monthIndex && d.getFullYear() === yearInt;
      });

      if (filteredPhotos.length === 0) {
        alert('Aucune photo trouvée pour ce mois et cette année.');
        return;
      }

      const zip = new JSZip();

      for (const p of filteredPhotos) {
        try {
          const response = await fetch(p.url);
          if (!response.ok) throw new Error(`Erreur de téléchargement de ${p.url}`);
          const blob = await response.blob();

          const metadata = p.metadata || {};
          const title = metadata.title ? metadata.title.toUpperCase().replace(/\s+/g, '_') : null;
          const date = p.timestamp ? new Date(p.timestamp).toLocaleDateString('fr-FR').replace(/\//g, '-') : null;
          const employeePseudo = metadata.TakenByPseudo ? metadata.TakenByPseudo.toUpperCase().replace(/\s+/g, '_') : null;

          let fileName = "FACTURE.png";
          if (date && title && employeePseudo) fileName = `${date}-${title}-${employeePseudo}.png`;
          else if (date && title) fileName = `${date}-${title}.png`;
          else if (date && employeePseudo) fileName = `${date}-${employeePseudo}.png`;
          else if (date) fileName = `${date}.png`;
          else if (title) fileName = `${title}.png`;
          else if (employeePseudo) fileName = `${employeePseudo}.png`;

          zip.file(fileName, blob);
        } catch (error) {
          console.error(`Erreur lors du téléchargement de la photo ${p.url} :`, error);
        }
      }

      const monthLabel = monthLabels[monthIndex] || 'Mois';
      zip.generateAsync({ type: 'blob' }).then((content) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(content);
        a.download = `${monthLabel}_${yearInt}.zip`;
        a.click();
      });
    } finally {
      setIsBulkModalOpen(false);
    }
  };

  const closeBulkModal = () => setIsBulkModalOpen(false);

  const handleBackClick = () => navigate('/merchantdocs');

  const formatTitle = (title) => {
    if (!title) return 'AUCUN TITRE';
    const text = sanitize(String(title)).trim();
    if (text.length > 10) return (text.substring(0, 10) + '...').toUpperCase();
    return text.toUpperCase();
  };

  // Photos triées (déjà triées au set, ici simple pass-through)
  const photosToRender = photos;

  return (
    <div className="dashboard-container">
      <MerchantNavbar />
      <style>{`
        .mi-scope { width: 100%; max-width: var(--eb-content-max); min-width: 0; }
        .mi-scope .eb-page-title { margin: 0; text-align: left; }
        .mi-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-left: auto; }
        .mi-icon-only { width: 42px; padding: 0; }

        .mi-scope .photo-grid.mi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: 16px; }
        .mi-scope .mi-grid .photo-item { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-xs); padding: 8px 8px 12px; text-align: left; min-width: 0; transition: box-shadow .15s ease, border-color .15s ease; }
        .mi-scope .mi-grid .photo-item:hover { border-color: var(--eb-border-strong); box-shadow: var(--eb-shadow-md); }
        .mi-scope .mi-grid .photo-image { width: 100%; height: auto; aspect-ratio: 3 / 4; display: block; border-radius: var(--eb-radius); box-shadow: none; }
        .mi-scope .mi-grid .photo-image:hover { transform: none; box-shadow: none; }
        .mi-scope .mi-grid .photo-item p { margin: 8px 4px 0; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .mi-scope .mi-grid .photo-item p.photo-title { font-weight: 600; color: var(--eb-text); }
        .mi-scope .mi-grid .photo-item p:last-of-type { margin-top: 2px; color: var(--eb-muted); }

        .mi-scope .mi-empty { grid-column: 1 / -1; margin: 0; padding: 40px 16px; text-align: center; color: var(--eb-muted); font-size: 14px; background: var(--eb-surface); border: 1px dashed var(--eb-border-strong); border-radius: var(--eb-radius-lg); }

        .mi-scope .mi-table-wrap { width: 100%; overflow-x: auto; }
        .mi-scope .dashboard-table { width: 100%; margin: 0; }
        .mi-scope .dashboard-table th svg { color: var(--eb-subtle); }
        .mi-scope .mi-thumb { display: block; width: 100%; max-width: 160px; height: auto; max-height: 200px; object-fit: contain; border-radius: var(--eb-radius-sm); border: 1px solid var(--eb-border); cursor: pointer; background: var(--eb-surface-2); }
        .mi-scope .mi-thumb-title { margin: 6px 0 0; font-size: 13px; }
        .mi-scope .mi-row-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 8px; }
        .mi-scope .dashboard-table th:last-child, .mi-scope .dashboard-table td:last-child { text-align: right; }
        .mi-scope .dashboard-table td:first-child, .mi-scope .dashboard-table th:first-child,
        .mi-scope .dashboard-table td:nth-child(2), .mi-scope .dashboard-table th:nth-child(2) { text-align: left; }

        /* Modale */
        .mi-overlay { position: fixed; inset: 0; z-index: 2000; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(15, 35, 56, .55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); }
        .mi-modal { width: 100%; max-width: 480px; max-height: calc(100dvh - 32px); overflow-y: auto; background: var(--eb-surface); border-radius: var(--eb-radius-xl); box-shadow: var(--eb-shadow-lg); padding: 24px; display: grid; gap: 16px; }
        .mi-modal-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .mi-modal-head h3 { margin: 0; font-size: 18px; font-weight: 700; text-align: left; color: var(--eb-text); }
        .mi-close { width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center; background: transparent; border: 0; border-radius: var(--eb-radius-sm); color: var(--eb-muted); cursor: pointer; }
        .mi-close:hover { background: var(--eb-surface-2); color: var(--eb-text); }
        .mi-modal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
        .mi-modal .eb-input { cursor: pointer; }
        .mi-modal-actions { display: flex; justify-content: center; gap: 10px; }

        @media (max-width: 1000px) {
          /* En-têtes : on n'affiche que les icônes */
          .mi-scope .dashboard-table thead .table-header-text { display: none; }
          .mi-scope .dashboard-table thead th svg { margin-right: 0 !important; }
          /* Boutons d'actions (ligne) : icônes seules */
          .mi-scope .dashboard-table tbody .button-text { display: none; }
          .mi-scope .dashboard-table tbody button svg { margin-right: 0 !important; }
        }
        @media (max-width: 768px) {
          .mi-scope .photo-grid.mi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
          .mi-scope .mi-thumb { max-width: 100%; max-height: 140px; }
          .mi-modal-grid { grid-template-columns: 1fr; }
          .mi-modal-actions { flex-direction: column-reverse; }
          .mi-modal-actions > button { width: 100%; }
        }
      `}</style>

      <div className="mi-scope">
      <header className="eb-page-header">
        <div>
          <h1 className="eb-page-title">Factures</h1>
          <p className="eb-page-subtitle">Toutes les factures importées par vous et votre équipe</p>
        </div>
        <div className="mi-actions">
          <button
            type="button"
            onClick={handleViewChange}
            title={isGridView ? 'Passer en vue tableau' : 'Passer en vue grille'}
            aria-label={isGridView ? 'Passer en vue tableau' : 'Passer en vue grille'}
            className={`eb-btn eb-btn--secondary${isNarrow ? ' mi-icon-only' : ''}`}
          >
            {isGridView
              ? <><FaList aria-hidden="true" />{!isNarrow && ' Tableau'}</>
              : <><FaTh aria-hidden="true" />{!isNarrow && ' Grille'}</>}
          </button>
          <button
            type="button"
            onClick={handleDownloadPhotosByMonth}
            title="Téléchargement groupé"
            aria-label="Téléchargement groupé"
            className={`eb-btn eb-btn--secondary${isNarrow ? ' mi-icon-only' : ''}`}
          >
            <FaDownload aria-hidden="true" />
            {!isNarrow && <> Téléchargement groupé</>}
          </button>
          <button
            type="button"
            onClick={handleImportClick}
            title="Importer"
            aria-label="Importer"
            className={`eb-btn eb-btn--primary${isNarrow ? ' mi-icon-only' : ''}`}
          >
            <FaUpload aria-hidden="true" />{!isNarrow && ' Importer'}
          </button>
        </div>
      </header>

      {/* Input file */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleImportInvoice}
      />

      {/* Bouton retour (masqué, conservé) */}
      <button
        onClick={handleBackClick}
        style={{ display: 'none' }}
      >
        <FaArrowLeft style={{ marginRight: '8px' }} /> Retour
      </button>

      {/* Zone contenu : grille ou tableau */}
      {isGridView ? (
        <div className="photo-grid mi-grid">
          {photosToRender.map((p) => (
            <div key={p.path || `${p.ownerUid || 'unknown'}-${p.name || 'no-name'}`} className="photo-item">
              <img
                src={p.url}
                alt="Historical"
                className="photo-image"
                loading="lazy"
                onClick={() => navigate('/merchantphotoviewer', { state: { photo: p } })}
              />
              <p
                className="photo-title"
                style={{ color: sanitize(p.metadata?.title) ? undefined : 'var(--eb-subtle)' }}
              >
                {formatTitle(p.metadata?.title)}
              </p>
              <p>
                {p.employeePseudo
                  ? (p.employeePseudo.length > 10 ? p.employeePseudo.substring(0, 10) + '...' : p.employeePseudo)
                  : 'Inconnu'}
              </p>
            </div>
          ))}
          {photosToRender.length === 0 && (
            <p className="mi-empty">Aucune facture pour le moment.</p>
          )}
        </div>
      ) : (
        <div className="mi-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th><FaCalendarAlt style={{ marginRight: '8px' }} /> <span className="table-header-text">Date & Heure</span></th>
                <th><FaUser style={{ marginRight: '8px' }} /> <span className="table-header-text">Employé(e)</span></th>
                <th><FaImage style={{ marginRight: '8px' }} /> <span className="table-header-text">Photo</span></th>
                <th><FaCog style={{ marginRight: '8px' }} /> <span className="table-header-text">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {photosToRender.map((p) => (
                <tr key={p.path || `${p.ownerUid || 'unknown'}-${p.name || 'no-name'}`}>
                  <td>{p.date}</td>
                  <td>
                    {p.employeePseudo
                      ? (p.employeePseudo.length > 10 ? p.employeePseudo.substring(0, 10) + '...' : p.employeePseudo)
                      : 'Inconnu'}
                  </td>
                  <td>
                    <img
                      src={p.url}
                      alt={p.name || "Image historique"}
                      className="photo-img mi-thumb"
                      loading="lazy"
                      onClick={() => navigate('/merchantphotoviewer', { state: { photo: p } })}
                    />
                    <p className="mi-thumb-title" style={{ color: sanitize(p.metadata?.title) ? 'var(--eb-text-2)' : 'var(--eb-subtle)' }}>
                      {sanitize(p.metadata?.title)
                        ? (p.metadata.title.length > 10 ? p.metadata.title.substring(0, 10) + "..." : p.metadata.title).toUpperCase()
                        : "Aucun titre"}
                    </p>
                  </td>
                  <td>
                    <div className="mi-row-actions">
                      <button className="button" onClick={() => handleDownloadPhoto(p)}>
                        <FaDownload style={{ marginRight: '8px' }} />
                        <span className="button-text">Télécharger</span>
                      </button>
                      <button className="delete-button" onClick={() => handleDeletePhoto(p)}>
                        <FaTrash style={{ marginRight: '8px' }} />
                        <span className="button-text">Supprimer</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {photosToRender.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--eb-muted)' }}>
                    Aucune facture pour le moment.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      </div>

      {/* =========================
          MODALE Téléchargement groupé
          ========================= */}
      {isBulkModalOpen && (
        <div className="mi-overlay" onClick={closeBulkModal}>
          <div
            className="mi-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mi-modal-head">
              <h3 id="bulk-modal-title">Téléchargement groupé</h3>
              <button onClick={closeBulkModal} aria-label="Fermer" className="mi-close">
                <FaTimes aria-hidden="true" />
              </button>
            </div>

            <p style={{ margin: 0, color: 'var(--eb-muted)', fontSize: 14 }}>Sélectionnez un mois et une année :</p>

            <div className="mi-modal-grid">
              {/* Mois */}
              <label className="eb-field">
                <span className="eb-label">Mois</span>
                <select
                  className="eb-input"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                >
                  {monthLabels.map((label, idx) => (
                    <option key={label} value={idx}>{label}</option>
                  ))}
                </select>
              </label>

              {/* Année */}
              <label className="eb-field">
                <span className="eb-label">Année</span>
                <select
                  className="eb-input"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {/* années de 2025 jusqu’à l’année courante, inclus */}
                  {(() => {
                    const yrs = [];
                    const currentYear = new Date().getFullYear();
                    for (let y = 2025; y <= currentYear; y++) {
                      yrs.push(y);
                    }
                    return yrs.reverse().map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ));
                  })()}
                </select>
              </label>
            </div>

            <div className="mi-modal-actions">
              <button onClick={closeBulkModal} className="eb-btn eb-btn--secondary">
                Annuler
              </button>
              <button onClick={confirmBulkDownload} className="eb-btn eb-btn--primary">
                <FaDownload aria-hidden="true" />
                Télécharger
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MerchantInvoices;
